import { memo, useContext } from 'react';

import { FormContext } from './utils';
import { Button } from '../buttons';
import type { ButtonProps } from '../buttons/utils';

/**
 * Пропсы кнопки сабмита формы: базовые пропсы Button + поведенческие флаги.
 *
 * `onClick`/`loading` не входят в API — перекрываются внутренней логикой
 * (onClick={handleSubmit}, loading={isSubmitting && !isSpinnerOff}).
 */
export interface SubmitButtonProps extends Omit<ButtonProps, 'onClick' | 'loading'> {
  /** Подсветить кнопку красным при невалидной форме после первой попытки сабмита. */
  redSubmitButtonIfError?: boolean;
  /** Внешний признак валидности значений (для redSubmitButtonIfError). */
  isValidValues?: boolean;
  /** Не показывать спиннер при isSubmitting. */
  isSpinnerOff?: boolean;
}

const SubmitButton = ({
  disabled,
  className,
  children,
  redSubmitButtonIfError = false,
  isValidValues = true,
  isSpinnerOff = false,
  type = 'submit',
  variant = 'primary',
  icon = 'check',
  ...rest
}: SubmitButtonProps) => {
  // FormContext из utils.js: при allowJs:false useContext возвращает unknown,
  // приводим к ожидаемой форме (Provider подаёт Formik-контекст).
  const {
    isSubmitting, handleSubmit, dirty, submitCount,
  } = useContext(FormContext) as {
    isSubmitting?: boolean;
    handleSubmit: any;
    dirty?: boolean;
    submitCount?: number;
  };

  return (
    <Button
      { ...rest }
      type={ type }
      icon={ icon }
      disabled={ disabled || isSubmitting }
      loading={ isSubmitting && !isSpinnerOff }
      className={ `btn-submit ${ className }` }
      onClick={ handleSubmit }
      variant={ (redSubmitButtonIfError && !isValidValues && dirty && submitCount > 0) ? 'danger' : (variant ?? 'primary') }
    >
      { children }
    </Button>
  );
};

export default memo(SubmitButton);
